import { CONSTANTS } from '@toolproof-core/visualization/_data/constants';
import type { CollectionKey, ConstellationKey, ItemKey } from '@toolproof-core/visualization/_contract/key';
import type {
    ConnectorItemParametricState,
    EntityItemParametricState,
    ItemBehaviorController,
    ItemInteractionIntent,
} from '@toolproof-core/visualization/_contract/item';
import type { SpaceDeriver } from '@toolproof-core/visualization/derivation/_contract/derivation';
import type {
    ConnectorItemCollection,
    EntityItemCollection,
} from '@toolproof-core/visualization/_contract/collection';
import type {
    ConnectorConstellation,
    EntityConstellation,
} from '@toolproof-core/visualization/_contract/constellation';

import { ELEMENT_COLORS } from '@/adapters/biology/_data/elementColors';
import type { BiologyCosmosData, ProteinSecondaryStructure, ProteinStructure } from '@/adapters/biology/_contract/proteinStructure';
import type { Config, ProteinSelection, RuntimeState } from '@/adapters/biology/_contract/config';
import { activeProteinStructure } from '@/adapters/biology/_pure/activeStructure';

const STRUCTURE_STYLE: Record<ProteinSecondaryStructure, { color: string; radius: number }> = {
    helix: { color: '#f08a4b', radius: 0.16 },
    sheet: { color: '#a16ae8', radius: 0.16 },
    loop: { color: '#35a7a0', radius: 0.075 },
};

function entityItemKey(id: string): ItemKey<'entity'> {
    return `item:entity:${id}`;
}

function connectorItemKey(id: string): ItemKey<'connector'> {
    return `item:connector:${id}`;
}

function entityCollectionKey(id: string): CollectionKey<'entity'> {
    return `collection:entity:${id}`;
}

function connectorCollectionKey(id: string): CollectionKey<'connector'> {
    return `collection:connector:${id}`;
}

function entityConstellationKey(id: string): ConstellationKey<'entity'> {
    return `constellation:entity:${id}`;
}

function connectorConstellationKey(id: string): ConstellationKey<'connector'> {
    return `constellation:connector:${id}`;
}

function sphereShape(radius: number) {
    return {
        shapeKind: CONSTANTS.Enums.ShapeKind.sphere,
        labelHostCapability: 'above-only',
        radius,
        anchorsByKey: { center: { anchorKey: 'center', localPosition: [0, 0, 0] as [number, number, number] } },
    } as const;
}

function entityConstellation(collection: EntityItemCollection): EntityConstellation {
    const firstItem = collection.items[0];
    if (!firstItem) throw new Error(`Entity collection ${collection.itemCollectionKey} is empty.`);
    return {
        constellationKind: CONSTANTS.Enums.ItemKind.entity,
        constellationKey: entityConstellationKey(collection.itemCollectionKey.slice('collection:entity:'.length)),
        itemCollectionKey: collection.itemCollectionKey,
        anchor: { itemKey: firstItem.itemKey, anchorKey: 'center' },
    };
}

function connectorConstellation(collection: ConnectorItemCollection): ConnectorConstellation {
    return {
        constellationKind: CONSTANTS.Enums.ItemKind.connector,
        constellationKey: connectorConstellationKey(collection.itemCollectionKey.slice('collection:connector:'.length)),
        itemCollectionKey: collection.itemCollectionKey,
    };
}

const SELECTION_COLOR = '#fff36d';
const NEIGHBOR_COLOR = '#ffd166';

function selectionsEqual(left: ProteinSelection | null, right: ProteinSelection): boolean {
    if (!left) return false;
    if (left.kind === 'atom' && right.kind === 'atom') return left.atomId === right.atomId;
    if (left.kind === 'residue' && right.kind === 'residue') {
        return left.chainId === right.chainId && left.sequence === right.sequence;
    }
    return false;
}

function deriveSelectionContext(structure: ProteinStructure, selection: ProteinSelection | null) {
    const selectedAtom = selection?.kind === 'atom'
        ? structure.atoms.find((atom) => atom.id === selection.atomId)
        : undefined;
    const selectedResidueKey = selection?.kind === 'residue'
        ? { chainId: selection.chainId, sequence: selection.sequence }
        : selectedAtom
            ? { chainId: selectedAtom.chainId, sequence: selectedAtom.residueSequence }
            : null;
    const selectedResidue = selectedResidueKey
        ? structure.chains.find((chain) => chain.id === selectedResidueKey.chainId)?.residues
            .find((residue) => residue.sequence === selectedResidueKey.sequence)
        : undefined;
    const selectedResidueAtoms = selectedResidueKey
        ? structure.atoms.filter((atom) =>
            atom.chainId === selectedResidueKey.chainId
            && atom.residueSequence === selectedResidueKey.sequence,
        )
        : [];
    const seedAtomIds = new Set(
        selection?.kind === 'atom' && selectedAtom
            ? [selectedAtom.id]
            : selectedResidueAtoms.map((atom) => atom.id),
    );
    const focusAtomIds = new Set(seedAtomIds);
    for (const bond of structure.bonds) {
        if (seedAtomIds.has(bond.atomIdA)) focusAtomIds.add(bond.atomIdB);
        if (seedAtomIds.has(bond.atomIdB)) focusAtomIds.add(bond.atomIdA);
    }
    const focusResidueKeys = new Set(
        structure.atoms.filter((atom) => focusAtomIds.has(atom.id))
            .map((atom) => `${atom.chainId}\u0000${atom.residueSequence}`),
    );
    return { selectedAtom, selectedResidue, selectedResidueKey, selectedResidueAtoms, focusAtomIds, focusResidueKeys };
}

function createProteinItems(structure: ProteinStructure, selection: ProteinSelection | null) {
    const {
        selectedAtom,
        selectedResidueKey,
        selectedResidueAtoms,
        focusAtomIds,
        focusResidueKeys,
    } = deriveSelectionContext(structure, selection);
    const atomItems: EntityItemCollection['items'] = structure.atoms.map((atom) => ({
        itemKey: entityItemKey(`atom-${encodeURIComponent(atom.id)}`),
        itemKind: CONSTANTS.Enums.ItemKind.entity,
        name: `${atom.name} · ${atom.element} · ${atom.residueSequence}`,
    }));
    const atomItemKeyById = new Map(
        structure.atoms.map((atom) => [atom.id, entityItemKey(`atom-${encodeURIComponent(atom.id)}`)]),
    );
    const atomEntityStates: Partial<Record<ItemKey<'entity'>, EntityItemParametricState>> = {};
    const selectionByItemKey = new Map<ItemKey<'entity'>, ProteinSelection>();
    for (const atom of structure.atoms) {
        selectionByItemKey.set(entityItemKey(`atom-${encodeURIComponent(atom.id)}`), {
            kind: 'atom',
            atomId: atom.id,
        });
    }
    for (const chain of structure.chains) {
        for (const residue of chain.residues) {
            selectionByItemKey.set(entityItemKey(`trace-${encodeURIComponent(chain.id)}-${residue.sequence}`), {
                kind: 'residue',
                chainId: chain.id,
                sequence: residue.sequence,
            });
        }
    }
    const selectedAtomForView = selectedAtom
        ?? selectedResidueAtoms.find((atom) => atom.name === 'CA')
        ?? selectedResidueAtoms[0];
    const selectedAtomKey = selectedAtomForView
        ? atomItemKeyById.get(selectedAtomForView.id)
        : undefined;
    for (const atom of structure.atoms) {
        const key = atomItemKeyById.get(atom.id);
        if (!key) throw new Error(`Protein atom ${atom.id} has no entity item key.`);
        const isSelected = key === selectedAtomKey;
        const inFocus = focusAtomIds.has(atom.id);
        atomEntityStates[key] = {
            position: atom.position,
            shape: sphereShape(structure.angstromToWorldScale * 0.32),
            visualStyle: {
                color: inFocus && !isSelected
                    ? NEIGHBOR_COLOR
                    : ELEMENT_COLORS[atom.element.toUpperCase()] ?? '#d070d0',
                ...(selection ? { opacity: inFocus ? 1 : 0.14 } : {}),
                ...(isSelected ? { activeHighlightColor: SELECTION_COLOR, highlightCue: 'border' as const } : {}),
            },
            interaction: { enabled: true },
            ...(isSelected ? {
                label: { aboveItem: { text: `${atom.name} · ${atom.element}`, color: '#ffffff', fontSize: 0.38 } },
            } : {}),
        };
    }

    const atomBondItems: ConnectorItemCollection['items'] = structure.bonds.map((bond, index) => {
        const from = atomItemKeyById.get(bond.atomIdA);
        const to = atomItemKeyById.get(bond.atomIdB);
        if (!from || !to) {
            throw new Error(`Protein bond ${bond.atomIdA}/${bond.atomIdB} references a missing atom.`);
        }
        return {
            itemKey: connectorItemKey(`bond-${index}`),
            itemKind: CONSTANTS.Enums.ItemKind.connector,
            shape: {
                shapeKind: CONSTANTS.Enums.ShapeKind.lineGuide,
                labelHostCapability: 'above-only',
                start: [0, 0, 0],
                end: [0, 0, 0],
                anchorsByKey: {
                    start: { anchorKey: 'start', localPosition: [0, 0, 0] },
                    mid: { anchorKey: 'mid', localPosition: [0, 0, 0] },
                    end: { anchorKey: 'end', localPosition: [0, 0, 0] },
                },
            },
            endpoints: {
                from: { endpointKind: 'static', itemKey: from, anchorKey: 'center' },
                to: { endpointKind: 'static', itemKey: to, anchorKey: 'center' },
            },
        };
    });
    const atomConnectorStates: Partial<Record<ItemKey<'connector'>, ConnectorItemParametricState>> = {};
    for (const [index, bond] of structure.bonds.entries()) {
        const bondKey = connectorItemKey(`bond-${index}`);
        const touchesFocus = focusAtomIds.has(bond.atomIdA) || focusAtomIds.has(bond.atomIdB);
        const touchesSelected = selectedAtomKey !== undefined
            && (atomItemKeyById.get(bond.atomIdA) === selectedAtomKey
                || atomItemKeyById.get(bond.atomIdB) === selectedAtomKey);
        atomConnectorStates[bondKey] = {
            thickness: structure.angstromToWorldScale * 0.22,
            strokeCount: bond.order,
            strokeSpacing: structure.angstromToWorldScale * 0.42,
            ...(bond.aromatic ? { innerStrokePattern: 'dashed' as const } : {}),
            visualStyle: {
                color: touchesFocus ? NEIGHBOR_COLOR : '#b7bdc8',
                ...(selection ? { opacity: touchesFocus ? 0.95 : 0.12 } : {}),
                ...(touchesSelected ? { activeHighlightColor: SELECTION_COLOR } : {}),
            },
            ...(touchesSelected && selectedAtomKey ? {
                highlightOnActiveOf: [{ constellationKey: entityConstellationKey('atoms'), itemKey: selectedAtomKey }],
            } : {}),
        };
    }

    const traceItems: EntityItemCollection['items'] = [];
    const traceConnectors: ConnectorItemCollection['items'] = [];
    const traceEntityStates: Partial<Record<ItemKey<'entity'>, EntityItemParametricState>> = {};
    const traceConnectorStates: Partial<Record<ItemKey<'connector'>, ConnectorItemParametricState>> = {};
    for (const chain of structure.chains) {
        for (const [index, residue] of chain.residues.entries()) {
            const key = entityItemKey(`trace-${encodeURIComponent(chain.id)}-${residue.sequence}`);
            const style = STRUCTURE_STYLE[residue.secondaryStructure];
            traceItems.push({
                itemKey: key,
                itemKind: CONSTANTS.Enums.ItemKind.entity,
                name: `${residue.name} ${residue.sequence}`,
            });
            traceEntityStates[key] = {
                position: residue.position,
                shape: sphereShape(style.radius),
                visualStyle: {
                    color: focusResidueKeys.has(`${chain.id}\u0000${residue.sequence}`)
                        && !(selectedResidueKey?.chainId === chain.id && selectedResidueKey.sequence === residue.sequence)
                        ? NEIGHBOR_COLOR
                        : style.color,
                    ...(selection ? {
                        opacity: focusResidueKeys.has(`${chain.id}\u0000${residue.sequence}`) ? 1 : 0.14,
                    } : {}),
                    ...(selectedResidueKey?.chainId === chain.id && selectedResidueKey.sequence === residue.sequence
                        ? { activeHighlightColor: SELECTION_COLOR, highlightCue: 'border' as const }
                        : {}),
                },
                interaction: { enabled: true },
                ...(selectedResidueKey?.chainId === chain.id && selectedResidueKey.sequence === residue.sequence
                    ? { label: { aboveItem: { text: `${residue.name} ${residue.sequence}`, color: '#ffffff', fontSize: 0.42 } } }
                    : {}),
            };
            const nextResidue = chain.residues[index + 1];
            if (!nextResidue || nextResidue.sequence !== residue.sequence + 1) continue;

            const nextKey = entityItemKey(`trace-${encodeURIComponent(chain.id)}-${nextResidue.sequence}`);
            const connectorKey = connectorItemKey(`trace-${encodeURIComponent(chain.id)}-${residue.sequence}`);
            traceConnectors.push({
                itemKey: connectorKey,
                itemKind: CONSTANTS.Enums.ItemKind.connector,
                shape: {
                    shapeKind: CONSTANTS.Enums.ShapeKind.lineGuide,
                    labelHostCapability: 'above-only',
                    start: [0, 0, 0],
                    end: [0, 0, 0],
                    anchorsByKey: {
                        start: { anchorKey: 'start', localPosition: [0, 0, 0] },
                        mid: { anchorKey: 'mid', localPosition: [0, 0, 0] },
                        end: { anchorKey: 'end', localPosition: [0, 0, 0] },
                    },
                },
                endpoints: {
                    from: { endpointKind: 'static', itemKey: key, anchorKey: 'center' },
                    to: { endpointKind: 'static', itemKey: nextKey, anchorKey: 'center' },
                },
            });
            const touchesFocus = focusResidueKeys.has(`${chain.id}\u0000${residue.sequence}`)
                || focusResidueKeys.has(`${chain.id}\u0000${nextResidue.sequence}`);
            const touchesSelected = selectedResidueKey?.chainId === chain.id
                && (selectedResidueKey.sequence === residue.sequence || selectedResidueKey.sequence === nextResidue.sequence);
            traceConnectorStates[connectorKey] = {
                thickness: style.radius * 2,
                visualStyle: {
                    color: touchesFocus ? NEIGHBOR_COLOR : style.color,
                    ...(selection ? { opacity: touchesFocus ? 0.95 : 0.12 } : {}),
                    ...(touchesSelected ? { activeHighlightColor: SELECTION_COLOR } : {}),
                },
                ...(touchesSelected ? {
                    highlightOnActiveOf: [{
                        constellationKey: entityConstellationKey('trace'),
                        itemKey: entityItemKey(`trace-${encodeURIComponent(chain.id)}-${selectedResidueKey.sequence}`),
                    }],
                } : {}),
            };
        }
    }

    return {
        atomEntityCollection: {
            itemCollectionKey: entityCollectionKey('atoms'),
            itemCollectionKind: CONSTANTS.Enums.ItemKind.entity,
            items: atomItems,
        },
        atomConnectorCollection: {
            itemCollectionKey: connectorCollectionKey('atom-bonds'),
            itemCollectionKind: CONSTANTS.Enums.ItemKind.connector,
            items: atomBondItems,
        },
        traceEntityCollection: {
            itemCollectionKey: entityCollectionKey('trace'),
            itemCollectionKind: CONSTANTS.Enums.ItemKind.entity,
            items: traceItems,
        },
        traceConnectorCollection: {
            itemCollectionKey: connectorCollectionKey('trace-bonds'),
            itemCollectionKind: CONSTANTS.Enums.ItemKind.connector,
            items: traceConnectors,
        },
        atomEntityStates,
        atomConnectorStates,
        traceEntityStates,
        traceConnectorStates,
        selectionByItemKey,
    };
}

export function createSpaceDeriver(): SpaceDeriver<BiologyCosmosData, Config, RuntimeState> {
    return (input) => {
        const structure = activeProteinStructure(input.runtimeState, input.cosmosData);
        if (!structure) {
            throw new Error('Biology-space requires its parsed mmCIF structure before deriving a scene.');
        }

        const collections = createProteinItems(structure, input.runtimeState.selection);
        const traceEntityConstellation = entityConstellation(collections.traceEntityCollection);
        const atomEntityConstellation = entityConstellation(collections.atomEntityCollection);
        const traceConnectorConstellation = connectorConstellation(collections.traceConnectorCollection);
        const atomConnectorConstellation = connectorConstellation(collections.atomConnectorCollection);
        const showTrace = input.runtimeState.view === 'trace';
        const selectionContext = deriveSelectionContext(structure, input.runtimeState.selection);
        const selectedTraceItemKey = selectionContext.selectedResidueKey
            ? entityItemKey(`trace-${encodeURIComponent(selectionContext.selectedResidueKey.chainId)}-${selectionContext.selectedResidueKey.sequence}`)
            : undefined;
        const selectedAtomForView = selectionContext.selectedAtom
            ?? selectionContext.selectedResidueAtoms.find((atom) => atom.name === 'CA')
            ?? selectionContext.selectedResidueAtoms[0];
        const selectedAtomItemKey = selectedAtomForView
            ? entityItemKey(`atom-${encodeURIComponent(selectedAtomForView.id)}`)
            : undefined;
        const activeItemKey = showTrace ? selectedTraceItemKey : selectedAtomItemKey;
        const activeConstellationKey = showTrace
            ? traceEntityConstellation.constellationKey
            : atomEntityConstellation.constellationKey;
        const itemBehaviorController: ItemBehaviorController = {
            onIntent: (intent: ItemInteractionIntent) => {
                if (intent.itemInteractionIntentKind !== 'item-clicked') return;
                const nextSelection = collections.selectionByItemKey.get(intent.itemKey);
                if (!nextSelection) return;
                input.config.runtimeCallbacks?.onRuntimeStateChange?.((current) => ({
                    ...current,
                    selection: selectionsEqual(current.selection, nextSelection) ? null : nextSelection,
                }));
            },
        };

        return {
            base: {
                spaceKey: input.spaceKey,
                itemCollections: [
                    collections.traceEntityCollection,
                    collections.traceConnectorCollection,
                    collections.atomEntityCollection,
                    collections.atomConnectorCollection,
                ],
                constellations: [
                    traceEntityConstellation,
                    traceConnectorConstellation,
                    atomEntityConstellation,
                    atomConnectorConstellation,
                ],
                itemParametricState: {
                    entityItemByKey: {
                        ...collections.traceEntityStates,
                        ...collections.atomEntityStates,
                    },
                    connectorItemByKey: {
                        ...collections.traceConnectorStates,
                        ...collections.atomConnectorStates,
                    },
                },
                itemBehaviorController,
                constellationParametricState: {
                    entityConstellationByKey: {
                        [traceEntityConstellation.constellationKey]: {
                            layout: {
                                layoutKind: CONSTANTS.Enums.LayoutKind.spoke,
                                anchor: { anchorKind: 'origin' },
                                direction: [0, 1, 0],
                                spacing: 0,
                                originNearestPosition: [0, 0, 0],
                            },
                            visibility: { visible: showTrace },
                        },
                        [atomEntityConstellation.constellationKey]: {
                            layout: {
                                layoutKind: CONSTANTS.Enums.LayoutKind.spoke,
                                anchor: { anchorKind: 'origin' },
                                direction: [0, 1, 0],
                                spacing: 0,
                                originNearestPosition: [0, 0, 0],
                            },
                            visibility: { visible: !showTrace },
                        },
                    },
                    connectorConstellationByKey: {
                        [traceConnectorConstellation.constellationKey]: { visibility: { visible: showTrace } },
                        [atomConnectorConstellation.constellationKey]: { visibility: { visible: !showTrace } },
                    },
                },
                spaceRenderOptions: {
                    backgroundColor: '#0b1020',
                    ...(activeItemKey ? {
                        activation: {
                            activeItemKeyByConstellationKey: {
                                [activeConstellationKey]: activeItemKey,
                            },
                        },
                    } : {}),
                    camera: {
                        pose: { position: [0, 0, 16], target: [0, 0, 0], fov: 50 },
                    },
                },
            },
        };
    };
}
